import { motion } from "framer-motion";
import type { ReactNode } from "react";

interface OptionCardProps {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  title?: string;
  className?: string;
}

/** Selectable card with motion hover/tap and a charcoal selected ring. */
export function OptionCard({ selected, onClick, children, title, className = "" }: OptionCardProps) {
  return (
    <motion.button
      type="button"
      title={title}
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border p-2 transition-colors ${
        selected
          ? "border-primary bg-surface-container-high/40"
          : "border-outline-variant/40 bg-surface-container-lowest hover:border-outline"
      } ${className}`}
    >
      {children}
    </motion.button>
  );
}

interface ChipProps {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
  /** Greyed out and inert. Used where a choice is real but unavailable for the
   *  CURRENT sibling selection — e.g. a prong style with no modelled asset for
   *  the chosen cut. Preferred over hiding it: the row keeps a stable shape as
   *  the user moves between cuts, and the absence is legible rather than a
   *  silently different set of options each time. */
  disabled?: boolean;
  /** Native tooltip, normally the reason a disabled chip is disabled. */
  title?: string;
}

/** Pill chip used for ratios, presets, finishes, sides, etc. */
export function Chip({ selected, onClick, children, className = "", disabled = false, title }: ChipProps) {
  return (
    <motion.button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      whileTap={disabled ? undefined : { scale: 0.96 }}
      className={`rounded-lg border px-3 py-2 font-display text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${
        disabled
          ? "cursor-not-allowed border-outline-variant/20 bg-surface/40 text-on-surface-variant/35"
          : selected
            ? "border-secondary bg-secondary-container/20 text-secondary"
            : "border-outline-variant/40 bg-surface text-on-surface-variant hover:border-outline"
      } ${className}`}
    >
      {children}
    </motion.button>
  );
}

interface SwatchProps {
  color: number;
  label: string;
  selected: boolean;
  onClick: () => void;
}

/** Round metal color swatch. */
export function Swatch({ color, label, selected, onClick }: SwatchProps) {
  const hex = `#${color.toString(16).padStart(6, "0")}`;
  return (
    <button type="button" title={label} onClick={onClick} className="flex flex-col items-center gap-1">
      <motion.span
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        className="h-10 w-10 rounded-full"
        style={{ background: hex, boxShadow: selected ? "0 0 0 2px #000, inset 0 0 0 2px #fff" : "inset 0 0 0 1px rgba(0,0,0,0.12)" }}
      />
      <span className="font-body text-[8px] text-on-surface-variant">{label}</span>
    </button>
  );
}
